import {
  type CurrentFiltersType,
  type FilterOption,
  type FilterTypeList,
  filterTypesEnum,
  type MultiEntitySelections,
} from "@/types/common/filters";
import { cn } from "@/utils/theme";
import { Button, Icon } from "@elaniintc/goes-design-system";
import { useEffect, useState } from "react";
import { FiltersModal } from "./FiltersModal";
import { PulseIndicator } from "@/components/common/pulse-indicator";

interface FilterProps {
  filters: FilterTypeList[];
  currentFilters: CurrentFiltersType[] | [];
  onCurrentFiltersChange:
    | React.Dispatch<React.SetStateAction<[] | CurrentFiltersType[]>>
    | ((filters: CurrentFiltersType[]) => void);
  filterButtonClass?: string;
  buttonSize?: "sm" | "default" | "lg";
  selecteds?: MultiEntitySelections;
  onSetSelecteds?: React.Dispatch<React.SetStateAction<MultiEntitySelections>>;
}
export default function Filter({
  filters,
  currentFilters,
  onCurrentFiltersChange,
  filterButtonClass = "",
  buttonSize = "default",
  selecteds = {},
  onSetSelecteds,
}: Readonly<FilterProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const [formatFilters, setFormatFilters] = useState<FilterOption[]>();

  useEffect(() => {
    const fixedFilters: FilterOption[] = [];
    filters.forEach((filter, index) => {
      const activeFilter = currentFilters.find((field) =>
        Object.keys(field).includes(filter.field),
      );

      switch (filter.type) {
        case filterTypesEnum.status: {
          const activeValues = activeFilter
            ? Object.values(activeFilter).flatMap((val) => val.split(","))
            : [];
          fixedFilters.push({
            id: index + 1,
            type: "status",
            label: "Estado",
            status: filter.options.map((item) => ({
              label: item.label,
              value: activeValues.includes(item.field),
              field: item.field,
            })),
            field: filter.field,
            result: activeFilter?.[filter.field]
              ? activeFilter?.[filter.field]
              : "",
            isUpdated: false,
          });
          break;
        }
        case filterTypesEnum.dates:
          fixedFilters.push({
            id: index + 1,
            type: "dates",
            label: filter.label,
            field: filter.field,
            result: activeFilter?.[filter.field]
              ? activeFilter?.[filter.field]
              : "",
            isUpdated: false,
            shouldDisabledFutureDates: true,
          });
          break;
        case filterTypesEnum.textsearch:
          fixedFilters.push({
            id: index + 1,
            type: "textsearch",
            label: filter.label,
            field: filter.field,
            searchField: filter.searchField,
            searchType: filter.searchType,
            query: filter.query,
            result: activeFilter?.[filter.field]
              ? activeFilter?.[filter.field]
              : "",
            isUpdated: false,
            selected: selecteds?.[filter.searchType] ?? [],
          });
          break;
        default:
          break;
      }
    });
    setFormatFilters(fixedFilters);
  }, [currentFilters, filters, isOpen, selecteds]);

  const handleFiltersUpdate = (newFilters: FilterOption[]) => {
    const mapped: CurrentFiltersType[] = [];
    const newSelections: MultiEntitySelections = { ...selecteds };

    newFilters.forEach((filter) => {
      if (filter.result) {
        mapped.push({
          [filter.field]: filter.result,
        });
      }
      if (filter.type === filterTypesEnum.textsearch) {
        const searchFilter = filter;
        if (searchFilter.searchType === "user") {
          newSelections["user"] =
            searchFilter.selected as MultiEntitySelections["user"];
        }
        if (searchFilter.searchType === "roles") {
          newSelections["roles"] =
            searchFilter.selected as MultiEntitySelections["roles"];
        }
        if (searchFilter.searchType === "tag") {
          newSelections["tag"] =
            searchFilter.selected as MultiEntitySelections["tag"];
        }
      }
    });

    if (onSetSelecteds) {
      onSetSelecteds(newSelections);
    }

    handleToggle();
    onCurrentFiltersChange([...mapped]);
  };

  const handleToggle = (type: string = "") => {
    if (type === "clean") {
      if (filters && filters.length > 0) onCurrentFiltersChange([]);
    }
    setIsOpen((prev) => !prev);
  };

  const hasCurrentFilters = currentFilters && currentFilters.length > 1; // with_insolvencies_count is always present in the current filters, so we check if there's more than 1

  return (
    <>
      {formatFilters !== undefined && isOpen && (
        <FiltersModal
          items={formatFilters}
          onApply={handleFiltersUpdate}
          isOpen={isOpen}
          onToggle={handleToggle}
          hasCurrentFilters={hasCurrentFilters}
        />
      )}

      <Button
        variant="quiet"
        size={buttonSize}
        className={cn(
          "leading-2xl shadow-custom py-1.5 px-2 rounded-lg min-w-9 gap-2 font-semibold text-body text-sm font-geist cursor-pointer relative",
          filterButtonClass,
        )}
        onClick={() => setIsOpen(true)}
      >
        Filtrar
        <Icon type="far" name="filter" />
        <PulseIndicator showIndicator={hasCurrentFilters} />
      </Button>
    </>
  );
}
